/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit,
} from '@angular/core';
import { DashboardService } from '../shared/dashboard.service';

// The "Top requested devices in your area" card, asking whether it helped. A "Not helpful" answer hides it for good.
@Component({
  selector: 'ut-dashboard-requested-devices',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard-requested-devices.component.html',
  styleUrls: ['./dashboard-requested-devices.component.scss'],
})
export class DashboardRequestedDevicesComponent implements OnInit {
  devices: any[] = [];
  askForFeedback = false;
  // Hidden until the feedback lookup returns, so a tester who said "Not helpful?" never sees the card flash up.
  hidden = true;
  thanks = '';

  constructor(
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.dashboardService.requestedDevices().then((devices) => {
      this.devices = devices || [];
      this.cdr.markForCheck();
    }, () => {
      // No devices, no card.
    });
    this.dashboardService.requestedDevicesFeedback().then((feedback) => {
      this.askForFeedback = !feedback;
      this.hidden = !!feedback && !feedback.is_positive;
      this.cdr.markForCheck();
    }, () => {
      // Without the tester's answer, stay hidden rather than ask again or override a "Not helpful?".
    });
  }

  helpful(): void {
    this.answer(true, 'Thanks for your feedback.');
    this.focus(this.host.nativeElement.querySelector('#dashboardRequestedTitle'));
  }

  // The card goes, so focus moves to the card above it in the same column.
  notHelpful(): void {
    this.hidden = true;
    this.answer(false, "Thanks for your feedback. We won't show these devices again.");
    this.focus(document.getElementById('dashboardDevicesTitle'));
  }

  // Updates the card right away so focus has somewhere to go, then confirms once the answer is saved.
  private answer(isPositive: boolean, thanks: string): void {
    this.askForFeedback = false;
    this.thanks = '';
    this.cdr.detectChanges();
    this.dashboardService.sendRequestedDevicesFeedback(isPositive).then(() => {
      this.thanks = thanks;
      this.cdr.markForCheck();
    }, () => {
      this.askForFeedback = true;
      this.hidden = false;
      this.thanks = "Your feedback couldn't be sent. Please try again.";
      this.cdr.markForCheck();
    });
  }

  private focus(target: Element | null): void {
    (target as HTMLElement | null)?.focus();
  }
}
